/*
  Vse, kar se dotika baze, je tu. Komponente ne kličejo Supabase neposredno.

  Pravilo: vsaka funkcija bodisi vrne podatke bodisi vrže napako z berljivim
  slovenskim sporočilom. Nobena ne kliče alert() — kaj se pokaže uporabniku,
  odloči komponenta.
*/

import { sb } from './supabase.js';
import { ADMIN_EMAIL, MAX_DOC_BYTES, DOC_IMAGE_MAX_PX, PERUT_SOLA_ID } from '../config.js';
import { fmtDate, today } from './format.js';
import { sortByStevilka } from './progress.js';

/** Napaka, ki jo je varno pokazati uporabniku. */
export class AppError extends Error {
  constructor(message, cause) {
    super(message);
    this.name = 'AppError';
    this.cause = cause;
  }
}

/** Supabase napako prevede v stavek, ki nekaj pove. */
function explain(error, fallback) {
  if (!error) return null;
  if (error.code === '23505') return 'Ta zapis že obstaja.';
  if (error.code === '42501' || error.message?.includes('row-level security')) {
    return 'Za to dejanje nimaš pravic.';
  }
  if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
    return 'Ni povezave. Preveri internet in poskusi znova.';
  }
  return `${fallback} (${error.message})`;
}

function must(error, fallback) {
  if (error) throw new AppError(explain(error, fallback), error);
}

/*
  Zaklep za predogled.

  Ko inštruktor gleda aplikacijo skozi oči tečajnika, se ne sme nič zapisati
  — sicer bi lahko pomotoma potrdil vajo, oddal test ali naložil dokument v
  ime tečajnika, in to bi bil pravi zapis v evidenci.

  Zaklep je namenoma TU in ne v komponentah: tako ni mogoče pozabiti na
  kakšen gumb. Vmesnik pisalne možnosti skrije, ta vrstica pa je varovalo,
  če bi se katera vseeno prikradla skozi.
*/
let previewLock = false;

export const setPreviewLock = (on) => {
  previewLock = on;
};

function assertWritable() {
  if (previewLock) {
    throw new AppError('Predogled tečajnikovega pogleda — spreminjanje tu ni mogoče.');
  }
}

/* ── Branje ────────────────────────────────────────────────── */

/** Naloži celo tabelo v paketih po 1000 — PostgREST več naenkrat ne vrne. */
export async function fetchAll(table, order = ['id']) {
  const out = [];
  const size = 1000;
  for (let from = 0; ; from += size) {
    let q = sb.from(table).select('*');
    order.forEach((c) => {
      q = q.order(c, { ascending: true });
    });
    const { data, error } = await q.range(from, from + size - 1);
    must(error, `Napaka pri branju tabele ${table}.`);
    out.push(...(data ?? []));
    if (!data || data.length < size) break;
  }
  return out;
}

/** Vse vrstice, kjer je `col` med `vals` — v paketih, da naslov ne preseže omejitve. */
export async function fetchAllIn(table, col, vals, order = ['id']) {
  const out = [];
  const ids = [...new Set(vals ?? [])];
  for (let i = 0; i < ids.length; i += 150) {
    const chunk = ids.slice(i, i + 150);
    for (let from = 0; ; from += 1000) {
      let q = sb.from(table).select('*').in(col, chunk);
      order.forEach((c) => {
        q = q.order(c, { ascending: true });
      });
      const { data, error } = await q.range(from, from + 999);
      must(error, `Napaka pri branju tabele ${table}.`);
      out.push(...(data ?? []));
      if (!data || data.length < 1000) break;
    }
  }
  return out;
}

/** Vrstice iz `progress` v obliko { exId: { subs: [...], comment } }. */
export function shapeProgress(rows) {
  const map = {};
  (rows ?? []).forEach((r) => {
    if (!map[r.exercise_id]) map[r.exercise_id] = { subs: [] };
    if (r.sub_index !== null && r.sub_index >= 0) {
      map[r.exercise_id].subs[r.sub_index] = {
        done: r.done,
        date: r.done_date,
        status: r.status,
        comment: r.comment,
        instructor_comment: r.instructor_comment,
      };
    }
    if (r.sub_index === -1) {
      map[r.exercise_id].comment = r.comment;
      map[r.exercise_id].instructor_comment = r.instructor_comment;
    }
  });
  return map;
}

export async function getProfile(uid) {
  const { data, error } = await sb.from('profiles').select('*').eq('id', uid).maybeSingle();
  must(error, 'Napaka pri branju profila.');
  return data;
}

/** Vse, kar tečajnik potrebuje za svoj pogled — v enem zamahu. */
export async function loadStudent(uid) {
  const [pr, fl, att, docs, exams, uc] = await Promise.all([
    sb.from('progress').select('*').eq('user_id', uid),
    sb.from('flights').select('*').eq('user_id', uid).order('flight_date', { ascending: false }),
    sb
      .from('attendance')
      .select('*')
      .eq('user_id', uid)
      .order('attendance_date', { ascending: false }),
    loadDocMap(uid),
    loadExamAttempts(uid),
    sb.from('ucenje_rezultati').select('*').eq('user_id', uid),
  ]);
  must(pr.error, 'Napaka pri branju napredka.');
  must(fl.error, 'Napaka pri branju knjižice letenja.');
  must(att.error, 'Napaka pri branju prisotnosti.');
  return {
    progress: shapeProgress(pr.data),
    flights: fl.data ?? [],
    attendance: att.data ?? [],
    docs,
    exams,
    // Interaktivno učenje ni ključno: če branje spodleti, se pogled vseeno naloži.
    ucenje: uc.data ?? [],
  };
}

/**
 * Vse, kar potrebuje inštruktor — za ENO šolo.
 * `clanEmails` so inštruktorji te šole: njihovi profili niso tečajniki.
 */
export async function loadAdmin(solaId = PERUT_SOLA_ID, clanEmails = [ADMIN_EMAIL]) {
  const { data: usersRaw, error: eU } = await sb
    .from('profiles')
    .select('*')
    .eq('sola_id', solaId)
    .order('id', { ascending: true });
  must(eU, 'Napaka pri branju tečajnikov.');
  const clani = new Set((clanEmails ?? []).map((e) => e.toLowerCase()));
  const users = (usersRaw ?? []).filter((u) => !clani.has((u.email ?? '').toLowerCase()));
  const ids = users.map((u) => u.id);

  const [pr, fl, att, notes, exams, uc] = await Promise.all([
    fetchAllIn('progress', 'user_id', ids),
    fetchAllIn('flights', 'user_id', ids),
    fetchAllIn('attendance', 'user_id', ids),
    fetchAllIn('admin_notes', 'user_id', ids, ['user_id']).catch(() => []),
    fetchAllIn('exam_attempts', 'user_id', ids, ['submitted_at']),
    fetchAllIn('ucenje_rezultati', 'user_id', ids, ['user_id', 'modul', 'kljuc']).catch(() => []),
  ]);
  exams.sort((a, b) => (b.submitted_at ?? '').localeCompare(a.submitted_at ?? ''));

  const noteMap = {};
  notes.forEach((n) => {
    noteMap[n.user_id] = n.note;
  });

  const list = users.map((u) => ({ ...u, admin_notes: noteMap[u.id] ?? null }));

  const progress = {};
  const flights = {};
  const attendance = {};
  const examsByUser = {};
  const ucenje = {};
  list.forEach((u) => {
    ucenje[u.id] = [];
    progress[u.id] = {};
    flights[u.id] = [];
    attendance[u.id] = [];
    examsByUser[u.id] = [];
  });
  exams.forEach((e) => examsByUser[e.user_id]?.push(e));
  uc.forEach((r) => ucenje[r.user_id]?.push(r));

  pr.forEach((r) => {
    const map = progress[r.user_id];
    if (!map) return;
    if (!map[r.exercise_id]) map[r.exercise_id] = { subs: [] };
    if (r.sub_index >= 0) {
      map[r.exercise_id].subs[r.sub_index] = {
        done: r.done,
        date: r.done_date,
        status: r.status,
      };
    }
    if (r.sub_index === -1) {
      map[r.exercise_id].comment = r.comment;
      map[r.exercise_id].instructor_comment = r.instructor_comment;
    }
  });

  fl.forEach((f) => flights[f.user_id]?.push(f));
  att.forEach((a) => attendance[a.user_id]?.push(a));
  Object.values(flights).forEach((l) =>
    l.sort((a, b) => (b.flight_date ?? '').localeCompare(a.flight_date ?? '')),
  );
  Object.values(attendance).forEach((l) =>
    l.sort((a, b) => (b.attendance_date ?? '').localeCompare(a.attendance_date ?? '')),
  );

  return {
    users: list.slice().sort(sortByStevilka),
    progress,
    flights,
    attendance,
    exams: examsByUser,
    ucenje,
  };
}

export async function loadExplanations(solaId = PERUT_SOLA_ID) {
  const { data, error } = await sb.from('exercise_content').select('*').eq('sola_id', solaId);
  must(error, 'Napaka pri branju razlag.');
  const map = {};
  (data ?? []).forEach((r) => {
    map[`${r.exercise_id}:${r.sub_index}`] = {
      zakaj: r.zakaj ?? '',
      kako: r.kako ?? '',
      napake: r.napake ?? '',
      varnost: r.varnost ?? '',
      video_url: r.video_url ?? '',
      ucbenik_sklic: r.ucbenik_sklic ?? '',
    };
  });
  return map;
}

/* ── Prijava ───────────────────────────────────────────────── */

export const signInWithGoogle = () =>
  sb.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin },
  });

export async function signOut() {
  // Podatkovni predpomnilnik je vezan na prijavljenega uporabnika. Na
  // deljeni napravi bi sicer naslednji uporabnik videl tuje podatke.
  try {
    navigator.serviceWorker?.controller?.postMessage('perut:signout');
  } catch {
    /* brez service workerja ni kaj čistiti */
  }
  return sb.auth.signOut();
}

/* ── Tečajnik piše ─────────────────────────────────────────── */

export async function saveProfileForm(uid, email, form, solaId = null) {
  assertWritable();
  const parts = form.ime.trim().split(' ');
  const ime = parts[0] ?? '';
  const priimek = parts.slice(1).join(' ') || form.priimek || '';
  const { error } = await sb.from('profiles').upsert({
    id: uid,
    email,
    ...(solaId ? { sola_id: solaId } : {}),
    ime,
    priimek,
    datum_rojstva: form.datum_rojstva || null,
    kraj_rojstva: form.kraj_rojstva,
    naslov: form.naslov,
    posta: form.posta,
    telefon: form.telefon,
    datum_tecaja: form.datum_tecaja || null,
    visina_cm: parseInt(form.visina_cm, 10) || null,
    teza_kg: parseInt(form.teza_kg, 10) || null,
    zdravstvene_omejitve: form.zdravstvene_omejitve,
    kontaktna_oseba: form.kontaktna_oseba,
    dodatna_vprasanja: form.dodatna_vprasanja,
    izjava_sprejeta: form.izjava_sprejeta,
    // Neobvezno; prazno pusti stolpec prazen, ne prazen niz.
    vir_prihoda: form.vir_prihoda || null,
  });
  must(error, 'Napaka pri shranjevanju podatkov.');
}

export async function toggleSub(uid, exId, si, done) {
  assertWritable();
  const { error } = await sb.from('progress').upsert(
    {
      user_id: uid,
      exercise_id: exId,
      sub_index: si,
      done,
      done_date: done ? today() : null,
      status: done ? 'pending' : null,
    },
    { onConflict: 'user_id,exercise_id,sub_index' },
  );
  must(error, 'Napaka pri shranjevanju vaje.');
}

export async function setSubDate(uid, exId, si, date) {
  assertWritable();
  const { error } = await sb
    .from('progress')
    .upsert(
      { user_id: uid, exercise_id: exId, sub_index: si, done_date: date },
      { onConflict: 'user_id,exercise_id,sub_index' },
    );
  must(error, 'Napaka pri shranjevanju datuma.');
}

export async function saveExComment(uid, exId, comment) {
  assertWritable();
  const { error } = await sb
    .from('progress')
    .upsert(
      { user_id: uid, exercise_id: exId, sub_index: -1, comment },
      { onConflict: 'user_id,exercise_id,sub_index' },
    );
  must(error, 'Napaka pri shranjevanju opombe.');
}

export async function addFlight(uid, flight) {
  assertWritable();
  const { error } = await sb.from('flights').insert({ user_id: uid, ...flight, status: 'pending' });
  must(error, 'Napaka pri dodajanju leta.');
}

export async function deleteFlight(id) {
  assertWritable();
  const { error } = await sb.from('flights').delete().eq('id', id);
  must(error, 'Napaka pri brisanju leta.');
}

export async function addAttendance(uid, { attendance_date, note }) {
  assertWritable();
  const { error } = await sb
    .from('attendance')
    .insert({ user_id: uid, attendance_date, note: note || null });
  if (error?.code === '23505') throw new AppError('Ta datum je v dnevniku že vpisan.', error);
  must(error, 'Napaka pri dodajanju dneva.');
}

export async function deleteAttendance(id) {
  assertWritable();
  const { error } = await sb.from('attendance').delete().eq('id', id);
  must(error, 'Napaka pri brisanju dneva.');
}

/**
  Interaktivno učenje: opravljena stopnja ali rezultat preverjanja znanja.
  Pri preverjanju se hrani najboljši rezultat — to odloči klicatelj.
*/
export async function saveUcenje(uid, modul, kljuc, rezultat = null) {
  assertWritable();
  const { error } = await sb.from('ucenje_rezultati').upsert(
    {
      user_id: uid,
      modul,
      kljuc,
      pravilnih: rezultat?.pravilnih ?? null,
      vseh: rezultat?.vseh ?? null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id,modul,kljuc' },
  );
  must(error, 'Napaka pri shranjevanju napredka učenja.');
}

/* ── Inštruktor piše ───────────────────────────────────────── */

export async function adminConfirmSub(uid, exId, si, action) {
  assertWritable();
  const { error } = await sb.from('progress').upsert(
    {
      user_id: uid,
      exercise_id: exId,
      sub_index: si,
      status: action,
      done: action === 'confirmed',
    },
    { onConflict: 'user_id,exercise_id,sub_index' },
  );
  must(error, 'Napaka pri potrjevanju vaje.');
}

export async function adminConfirmFlight(flightId, action) {
  assertWritable();
  const { error } = await sb.from('flights').update({ status: action }).eq('id', flightId);
  must(error, 'Napaka pri potrjevanju leta.');
}

export async function adminConfirmExercise(uid, exId) {
  assertWritable();
  const { error } = await sb
    .from('progress')
    .update({ status: 'confirmed', done: true })
    .eq('user_id', uid)
    .eq('exercise_id', exId)
    .gte('sub_index', 0)
    .eq('status', 'pending')
    .eq('done', true);
  must(error, 'Napaka pri potrjevanju vaje.');
}

/** Masovno potrjevanje za enega ali za vse tečajnike. */
export async function adminConfirmAll(ids) {
  assertWritable();
  const r1 = await sb
    .from('progress')
    .update({ status: 'confirmed', done: true })
    .in('user_id', ids)
    .gte('sub_index', 0)
    .eq('status', 'pending')
    .eq('done', true);
  const r2 = await sb
    .from('flights')
    .update({ status: 'confirmed' })
    .in('user_id', ids)
    .eq('status', 'pending');
  must(r1.error ?? r2.error, 'Napaka pri masovnem potrjevanju.');
}

export async function adminSaveComment(uid, exId, comment) {
  assertWritable();
  const { error } = await sb
    .from('progress')
    .upsert(
      { user_id: uid, exercise_id: exId, sub_index: -1, instructor_comment: comment },
      { onConflict: 'user_id,exercise_id,sub_index' },
    );
  must(error, 'Napaka pri shranjevanju komentarja.');
}

export async function adminToggleTeorija(uid, done) {
  assertWritable();
  const { error } = await sb
    .from('profiles')
    .update({ teorija_datum: done ? today() : null })
    .eq('id', uid);
  must(error, 'Napaka pri označevanju teorije.');
}

export async function updateProfileFields(uid, fields) {
  assertWritable();
  const { error } = await sb.from('profiles').update(fields).eq('id', uid);
  if (error?.code === '23505') {
    throw new AppError('Ta številka je že dodeljena drugemu tečajniku.', error);
  }
  must(error, 'Napaka pri shranjevanju.');
}

export async function saveAdminNotes(uid, note) {
  assertWritable();
  const { error } = await sb.from('admin_notes').upsert(
    {
      user_id: uid,
      note: note.trim() || null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'user_id' },
  );
  must(error, 'Napaka pri shranjevanju opomb.');
}

/** Trajno brisanje: otroci najprej, potem profil. */
export async function deleteStudent(uid) {
  assertWritable();
  for (const t of ['progress', 'flights', 'attendance']) {
    const { error } = await sb.from(t).delete().eq('user_id', uid);
    must(error, `Napaka pri brisanju (${t}).`);
  }
  const { error } = await sb.from('profiles').delete().eq('id', uid);
  must(error, 'Napaka pri brisanju profila.');
}

export async function saveExerciseContent(solaId, exId, subIndex, form) {
  assertWritable();
  const { error } = await sb.from('exercise_content').upsert(
    {
      sola_id: solaId,
      exercise_id: exId,
      sub_index: subIndex,
      zakaj: form.zakaj || null,
      kako: form.kako || null,
      napake: form.napake || null,
      varnost: form.varnost || null,
      video_url: form.video_url || null,
      ucbenik_sklic: form.ucbenik_sklic || null,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'sola_id,exercise_id,sub_index' },
  );
  must(error, 'Napaka pri shranjevanju razlage.');
}

/**
 * Sestavi vrstice za `progress`, ki jih dan na terenu doda.
 * Pod-vaje, ki jih tečajnik že ima potrjene, preskoči — sicer bi jim
 * povozili izvirni datum opravljanja.
 *
 * Ločeno od zapisovanja, da je lahko testirano brez baze.
 */
export function buildFieldDayRows({ ids, subKeys, date, progressByUser }) {
  const rows = [];
  ids.forEach((uid) =>
    subKeys.forEach((k) => {
      const [exId, siStr] = k.split(':');
      const si = parseInt(siStr, 10);
      if (progressByUser?.[uid]?.[exId]?.subs?.[si]?.status === 'confirmed') return;
      rows.push({
        user_id: uid,
        exercise_id: exId,
        sub_index: si,
        done: true,
        done_date: date,
        status: 'confirmed',
        by_instructor: true,
      });
    }),
  );
  return rows;
}

/**
 * Dan na terenu: prisotnost + potrjene vaje + leti za vse izbrane naenkrat.
 *
 * Vsak od treh delov lahko pade zase. Namesto da bi ob prvi napaki odnehali,
 * naredimo vse tri in poročamo, kaj je uspelo — inštruktor na terenu mora
 * vedeti, kaj je shranjeno, ne le da „je nekaj šlo narobe“.
 */
export async function saveFieldDay({ ids, subKeys, date, vzletisce, note, leti, progressByUser }) {
  assertWritable();
  const errs = [];

  const att = ids.map((uid) =>
    note ? { user_id: uid, attendance_date: date, note } : { user_id: uid, attendance_date: date },
  );
  const rA = await sb
    .from('attendance')
    .upsert(
      att,
      note
        ? { onConflict: 'user_id,attendance_date' }
        : { onConflict: 'user_id,attendance_date', ignoreDuplicates: true },
    );
  if (rA.error) errs.push('prisotnost: ' + rA.error.message);

  const rows = buildFieldDayRows({ ids, subKeys, date, progressByUser });
  if (rows.length) {
    const rP = await sb
      .from('progress')
      .upsert(rows, { onConflict: 'user_id,exercise_id,sub_index' });
    if (rP.error) errs.push('vaje: ' + rP.error.message);
  }

  let nFlights = 0;
  if (leti > 0) {
    const fl = [];
    ids.forEach((uid) => {
      for (let i = 0; i < leti; i++) {
        fl.push({
          user_id: uid,
          flight_date: date,
          vzletisce,
          opomba: note || null,
          status: 'confirmed',
        });
      }
    });
    const rF = await sb.from('flights').insert(fl);
    if (rF.error) errs.push('leti: ' + rF.error.message);
    else nFlights = fl.length;
  }

  return { errs, nStudents: ids.length, nSubs: rows.length, nFlights };
}

/* ── Dokumenti ─────────────────────────────────────────────────
   Bucket `documents` je ZASEBEN. Nikoli getPublicUrl — vedno
   podpisane povezave, ki veljajo eno uro.                          */

export async function loadDocMap(uid) {
  const { data: files, error } = await sb.storage.from('documents').list(uid, { limit: 50 });
  if (error || !files) return {};
  const real = files.filter((f) => f.name && !f.name.startsWith('.'));
  if (!real.length) return {};
  const { data: signed } = await sb.storage
    .from('documents')
    .createSignedUrls(
      real.map((f) => `${uid}/${f.name}`),
      3600,
    );
  const map = {};
  real.forEach((f, i) => {
    const docId = f.name.replace(/\.[^.]+$/, '');
    map[docId] = {
      name: f.name,
      path: `${uid}/${f.name}`,
      url: signed?.[i]?.signedUrl ?? '',
      uploadedAt: fmtDate((f.updated_at ?? f.created_at ?? '').slice(0, 10)),
      size: f.metadata?.size ?? null,
    };
  });
  return map;
}

/**
 * Fotografijo s telefona pomanjša, preden gre v Storage.
 *
 * Telefon posname 4–12 MB sliko. Za berljivo osebno izkaznico je 2000px
 * povsem dovolj, rezultat pa je pod 500 kB. Brez tega tečajniki na terenu
 * z mobilnim omrežjem nalaganja pogosto sploh ne dokončajo.
 *
 * Če stiskanje iz kakršnegakoli razloga ne uspe, vrne izvirno datoteko.
 */
export async function compressImage(file) {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, DOC_IMAGE_MAX_PX / Math.max(bitmap.width, bitmap.height));
    if (scale === 1 && file.size <= 1_500_000) return file;

    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.85));
    if (!blob || blob.size >= file.size) return file;
    return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/**
 * Naloži dokument. Vrne novo stanje za ta docId.
 *
 * Prejšnja verzija je pisala na pot `{docId}.{ext}` in ob zamenjavi
 * jpg → png pustila staro datoteko v bucketu. Ostala je kot sirota in
 * `list()` je vračal obe. Zdaj vse druge končnice za isti docId pobrišemo.
 */
export async function uploadDoc(uid, docId, rawFile) {
  assertWritable();
  const file = await compressImage(rawFile);
  if (file.size > MAX_DOC_BYTES) {
    throw new AppError(
      `Datoteka je prevelika (${Math.round(file.size / 1024 / 1024)} MB). Največ 8 MB.`,
    );
  }

  const ext = (file.name.split('.').pop() ?? 'jpg').toLowerCase();
  const path = `${uid}/${docId}.${ext}`;

  const { error } = await sb.storage.from('documents').upload(path, file, { upsert: true });
  must(error, 'Napaka pri nalaganju dokumenta.');

  // Počisti morebitne starejše različice istega dokumenta z drugo končnico.
  const { data: files } = await sb.storage.from('documents').list(uid, { limit: 50 });
  const stale = (files ?? [])
    .filter((f) => f.name.startsWith(docId + '.') && f.name !== `${docId}.${ext}`)
    .map((f) => `${uid}/${f.name}`);
  if (stale.length) await sb.storage.from('documents').remove(stale);

  const { data: signed } = await sb.storage.from('documents').createSignedUrl(path, 3600);
  return {
    name: file.name,
    path,
    url: signed?.signedUrl ?? '',
    uploadedAt: fmtDate(today()),
    size: file.size,
  };
}

/* ── Zaključni test ────────────────────────────────────────────
   Tečajnik bere vprašanja iz pogleda BREZ pravilnih odgovorov in odda
   prek funkcije submit_exam(), ki oceni v bazi. Ključ nikoli ne pride
   do brskalnika tečajnika.                                              */

export async function loadExamQuestions() {
  const { data, error } = await sb
    .from('exam_questions_public')
    .select('*')
    .order('ord', { ascending: true });
  must(error, 'Napaka pri branju vprašanj.');
  return data ?? [];
}

export async function loadExamThreshold() {
  const { data, error } = await sb.rpc('exam_pass_threshold');
  if (error) return 80; // prag je le za prikaz; oceni ga tako ali tako baza
  return data ?? 80;
}

/** Odda test. `answers` je { [idVprašanja]: indeksIzbire }. */
export async function submitExam(answers, podpis) {
  assertWritable();
  const { data, error } = await sb.rpc('submit_exam', {
    p_answers: answers,
    p_podpis: podpis,
  });
  must(error, 'Testa ni bilo mogoče oddati.');
  return Array.isArray(data) ? data[0] : data;
}

/** Poskusi enega tečajnika, najnovejši prvi. */
export async function loadExamAttempts(uid) {
  let q = sb.from('exam_attempts').select('*').order('submitted_at', { ascending: false });
  if (uid) q = q.eq('user_id', uid);
  const { data, error } = await q;
  must(error, 'Napaka pri branju rezultatov testa.');
  return data ?? [];
}

/**
 * Pregled enega poskusa: kaj je tečajnik izbral in pri katerih vprašanjih
 * se je zmotil. Pravilni odgovor in razlago vrne SAMO pri zgrešenih —
 * pri pravilno odgovorjenih ni česa razlagati, ključ pa po nepotrebnem
 * ne uhaja. Ločevanje naredi funkcija v bazi, ne ta koda.
 */
export async function loadExamReview(attemptId) {
  const { data, error } = await sb.rpc('exam_review', { p_attempt: attemptId });
  must(error, 'Pregleda testa ni bilo mogoče naložiti.');
  return data ?? [];
}

/** Vprašanja s pravilnimi odgovori — samo za admina (RLS to tudi zahteva). */
export async function loadExamQuestionsAdmin(solaId = PERUT_SOLA_ID) {
  const { data, error } = await sb
    .from('exam_questions')
    .select('*')
    .eq('sola_id', solaId)
    .order('ord', { ascending: true });
  must(error, 'Napaka pri branju vprašanj.');
  return data ?? [];
}

export async function saveExamQuestion(id, fields) {
  assertWritable();
  const { error } = await sb
    .from('exam_questions')
    .update({ ...fields, updated_at: new Date().toISOString() })
    .eq('id', id);
  must(error, 'Napaka pri shranjevanju vprašanja.');
}

/**
 * Naloži sliko k vprašanju zaključnega testa.
 *
 * Gre v JAVNI bucket Assets (mapa `exam/`), zato je tu getPublicUrl
 * pravilen — pravilo „nikoli getPublicUrl" velja za bucket `documents`,
 * kjer so osebni dokumenti tečajnikov. Slika vaje ni osebni podatek.
 */
export async function addExamQuestion(solaId, fields) {
  assertWritable();
  const { data, error } = await sb
    .from('exam_questions')
    .insert({ sola_id: solaId, ...fields })
    .select()
    .single();
  must(error, 'Napaka pri dodajanju vprašanja.');
  return data;
}

export async function deleteExamQuestion(id) {
  assertWritable();
  const { error } = await sb.from('exam_questions').delete().eq('id', id);
  must(error, 'Napaka pri brisanju vprašanja.');
}

export async function uploadExamImage(solaId, ord, file) {
  assertWritable();
  if (!file.type.startsWith('image/')) {
    throw new AppError('Naloži sliko (JPG, PNG ali WEBP).');
  }
  const compressed = await compressImage(file);
  if (compressed.size > MAX_DOC_BYTES) {
    throw new AppError('Slika je prevelika. Največ 8 MB.');
  }

  const ext = (compressed.name.split('.').pop() ?? 'jpg').toLowerCase();
  // Pot vsebuje šolo: policy v Storage dovoli pisanje samo članom te šole.
  const dir = `exam/${solaId}`;
  const path = `${dir}/${ord}.${ext}`;

  const { error } = await sb.storage.from('Assets').upload(path, compressed, { upsert: true });
  must(error, 'Napaka pri nalaganju slike.');

  // Počisti starejše različice z drugo končnico, da ne ostanejo sirote.
  const { data: files } = await sb.storage.from('Assets').list(dir, { limit: 200 });
  const stale = (files ?? [])
    .filter((f) => f.name.startsWith(`${ord}.`) && f.name !== `${ord}.${ext}`)
    .map((f) => `${dir}/${f.name}`);
  if (stale.length) await sb.storage.from('Assets').remove(stale);

  const { data } = sb.storage.from('Assets').getPublicUrl(path);
  // Poizvedba v naslovu prisili brskalnik, da po zamenjavi naloži novo sliko.
  return `${data.publicUrl}?v=${Date.now()}`;
}

export async function deleteExamAttempt(id) {
  assertWritable();
  const { error } = await sb.from('exam_attempts').delete().eq('id', id);
  must(error, 'Napaka pri brisanju poskusa.');
}

/* ── Varstvo osebnih podatkov ──────────────────────────────────
   Tečajnik mora priti do svojih podatkov in jih znati odnesti s sabo,
   sicer je pravica do dostopa in prenosljivosti samo na papirju.        */

/** Tečajnik izbriše svojo fotografijo dokumenta. */
export async function deleteDoc(uid, doc) {
  assertWritable();
  const { error } = await sb.storage.from('documents').remove([doc.path ?? `${uid}/${doc.name}`]);
  must(error, 'Dokumenta ni bilo mogoče izbrisati.');
}

/** Odprte in rešene zahteve za izbris. Brez uid vrne vse (samo admin jih vidi). */
export async function loadZahteveIzbris(uid) {
  let q = sb.from('zahteve_izbris').select('*').order('vlozeno', { ascending: false });
  if (uid) q = q.eq('user_id', uid);
  const { data, error } = await q;
  must(error, 'Napaka pri branju zahtev.');
  return data ?? [];
}

export async function vloziZahtevoZaIzbris(uid, sporocilo) {
  assertWritable();
  const { error } = await sb
    .from('zahteve_izbris')
    .insert({ user_id: uid, sporocilo: sporocilo?.trim() || null });
  must(error, 'Zahteve ni bilo mogoče vložiti.');
}

export async function resiZahtevoZaIzbris(id, opomba) {
  assertWritable();
  const { error } = await sb
    .from('zahteve_izbris')
    .update({ resil: new Date().toISOString(), opomba_admin: opomba?.trim() || null })
    .eq('id', id);
  must(error, 'Zahteve ni bilo mogoče označiti za rešeno.');
}

/* ── Šole (več najemnikov) ───────────────────────────────────
   Vloge: platforma (Luka) vidi vse šole; inštruktor je član šole v
   sole_clani (po e-pošti); vsi ostali so tečajniki svoje šole.          */

/** Vloga prijavljenega: kje je inštruktor in ali je platforma. */
export async function loadVloga(session) {
  const email = (session?.user?.email ?? '').toLowerCase();
  const platforma = email === ADMIN_EMAIL;
  // Dopolni user_id člana ob prvi prijavi; če funkcije ni, ni ključno.
  try {
    await sb.rpc('povezi_clana');
  } catch {
    /* stara baza */
  }
  const { data, error } = await sb.from('sole_clani').select('*').eq('email', email);
  must(error, 'Napaka pri branju vloge.');
  return { platforma, clanstva: data ?? [] };
}

/** Imena šol, kjer je klicatelj član — za preklop v glavi. */
export async function loadSoleIme(ids) {
  const { data, error } = await sb.from('sole').select('id,ime,kratko').in('id', ids);
  must(error, 'Napaka pri branju šol.');
  return data ?? [];
}

export async function loadSola(id) {
  const { data, error } = await sb.from('sole').select('*').eq('id', id).maybeSingle();
  must(error, 'Napaka pri branju šole.');
  return data;
}

/** Program šole; prazen seznam pomeni privzeti (Perutov) program. */
export async function loadProgram(solaId) {
  const { data, error } = await sb
    .from('program_vaje')
    .select('*')
    .eq('sola_id', solaId)
    .order('sklop_ord', { ascending: true })
    .order('ord', { ascending: true });
  must(error, 'Napaka pri branju programa.');
  return data ?? [];
}

export async function saveProgram(solaId, rows) {
  assertWritable();
  const payload = rows.map((r) => ({
    sola_id: solaId,
    id: r.id,
    sklop: r.sklop ?? 'zacetni',
    sklop_ord: r.sklop_ord ?? 0,
    ord: r.ord ?? 0,
    title: r.title,
    subs: r.subs ?? [],
    skrito: !!r.skrito,
  }));
  const { error } = await sb.from('program_vaje').upsert(payload, { onConflict: 'sola_id,id' });
  must(error, 'Napaka pri shranjevanju programa.');
}

/** Šole, ki sprejemajo tečajnike — za izbiro ob vpisu (brez prijave). */
export async function loadAktivneSole() {
  const { data, error } = await sb.rpc('aktivne_sole');
  must(error, 'Napaka pri branju šol.');
  return data ?? [];
}

export async function solaPoKodi(koda) {
  const { data, error } = await sb.rpc('sola_po_kodi', { p_koda: koda });
  must(error, 'Napaka pri iskanju šole.');
  return Array.isArray(data) ? data[0] ?? null : data;
}

export async function updateSola(id, fields) {
  assertWritable();
  const { error } = await sb.from('sole').update(fields).eq('id', id);
  must(error, 'Napaka pri shranjevanju šole.');
}

/** Logotip šole v javni bucket Assets, mapa sole/<id>/. */
export async function uploadSolaLogo(solaId, file) {
  assertWritable();
  if (!file.type.startsWith('image/')) throw new AppError('Naloži sliko (PNG, SVG, JPG ali WEBP).');
  const compressed = file.type === 'image/svg+xml' ? file : await compressImage(file);
  if (compressed.size > 2 * 1024 * 1024) throw new AppError('Logotip je prevelik. Največ 2 MB.');
  const ext = (compressed.name.split('.').pop() ?? 'png').toLowerCase();
  const path = `sole/${solaId}/logo.${ext}`;
  const { error } = await sb.storage.from('Assets').upload(path, compressed, { upsert: true });
  must(error, 'Napaka pri nalaganju logotipa.');
  const { data } = sb.storage.from('Assets').getPublicUrl(path);
  const url = `${data.publicUrl}?v=${Date.now()}`;
  await updateSola(solaId, { logo_url: url });
  return url;
}

export async function loadClani(solaId) {
  const { data, error } = await sb
    .from('sole_clani')
    .select('*')
    .eq('sola_id', solaId)
    .order('created_at', { ascending: true });
  must(error, 'Napaka pri branju inštruktorjev.');
  return data ?? [];
}

export async function addClan(solaId, email, vloga = 'instruktor', ime = null) {
  assertWritable();
  const { error } = await sb
    .from('sole_clani')
    .insert({ sola_id: solaId, email: email.trim().toLowerCase(), vloga, ime: ime?.trim() || null });
  if (error?.code === '23505') throw new AppError('Ta e-pošta je že med inštruktorji.', error);
  must(error, 'Napaka pri dodajanju inštruktorja.');
}

export async function removeClan(id) {
  assertWritable();
  const { error } = await sb.from('sole_clani').delete().eq('id', id);
  must(error, 'Napaka pri odstranjevanju inštruktorja.');
}

/* Platforma */

export async function loadPlatforma() {
  const [sole, prijave, profili, clani] = await Promise.all([
    sb.from('sole').select('*').order('created_at', { ascending: true }),
    sb.from('sole_prijave').select('*').order('created_at', { ascending: false }),
    sb.from('profiles').select('id,sola_id,archived,testni,ime'),
    sb.from('sole_clani').select('sola_id,email'),
  ]);
  must(sole.error, 'Napaka pri branju šol.');
  must(prijave.error, 'Napaka pri branju prijav.');
  const stevci = {};
  const clanEmails = new Set((clani.data ?? []).map((c) => c.email));
  (profili.data ?? []).forEach((p) => {
    if (!p.ime || p.testni || p.archived) return;
    if (clanEmails.has((p.email ?? '').toLowerCase())) return;
    stevci[p.sola_id] = (stevci[p.sola_id] ?? 0) + 1;
  });
  return {
    sole: (sole.data ?? []).map((s) => ({ ...s, aktivnih: stevci[s.id] ?? 0 })),
    prijave: prijave.data ?? [],
  };
}

export async function odobriPrijavo({ prijavaId, slug, plan, veljaDo, max, program }) {
  assertWritable();
  const { data, error } = await sb.rpc('odobri_prijavo', {
    p_prijava: prijavaId,
    p_slug: slug,
    p_plan: plan,
    p_velja_do: veljaDo || null,
    p_max: max ?? null,
    p_program: program ?? null,
  });
  must(error, 'Prijave ni bilo mogoče odobriti.');
  return data;
}

export async function zavrniPrijavo(id, opomba) {
  assertWritable();
  const { error } = await sb
    .from('sole_prijave')
    .update({ status: 'zavrnjena', obravnavano: new Date().toISOString(), opomba: opomba || null })
    .eq('id', id);
  must(error, 'Napaka pri zavračanju prijave.');
}

/** Platforma ustvari šolo ročno z vsemi polji (npr. po telefonu). */
export async function ustvariSoloPlatforma(fields) {
  assertWritable();
  const { data, error } = await sb.from('sole').insert(fields).select().single();
  if (error?.code === '23505') throw new AppError('Ta oznaka (slug) ali koda je že zasedena.', error);
  must(error, 'Napaka pri ustvarjanju šole.');
  return data;
}

/**
 * Samopostrežna šola: ustvari šolo, lastnika in program takoj (30-dnevni
 * preizkus). Dela brez prijave (e-pošta iz obrazca) in s prijavo (e-pošta
 * iz Google računa — klicatelj postane lastnik). Vrne { id, slug, koda, email }.
 */
export async function ustvariSolo(f) {
  const { data, error } = await sb.rpc('ustvari_solo', {
    p_ime: f.ime_sole,
    p_drzava: f.drzava ?? 'SI',
    p_jezik: f.jezik ?? 'sl',
    p_email: f.email ?? '',
    p_kontakt_ime: f.kontakt_ime ?? '',
    p_telefon: f.telefon ?? '',
    p_splet: f.splet ?? '',
    p_st_tecajnikov: f.st_tecajnikov ?? '',
    p_program: f.program?.length ? f.program : null,
    p_sklopi: f.sklopi && Object.keys(f.sklopi).length ? f.sklopi : null,
    p_program_besedilo: f.program_besedilo ?? '',
    p_vir: f.vir ?? '',
  });
  must(error, 'Šole ni bilo mogoče ustvariti.');
  return data;
}

/** Platforma izbriše šolo brez tečajnikov (testne, zlorabe). */
export async function deleteSola(id) {
  assertWritable();
  const { error } = await sb.from('sole').delete().eq('id', id);
  if (error?.code === '23503') throw new AppError('Šola ima tečajnike — zapri jo, ne briši.', error);
  must(error, 'Šole ni bilo mogoče izbrisati.');
}

/** Javna prijava šole s strani „Za šole“. Brez prijave uporabnika. */
export async function oddajPrijavoSole(f) {
  const { data, error } = await sb.rpc('oddaj_prijavo_sole', {
    p_ime_sole: f.ime_sole,
    p_drzava: f.drzava,
    p_jezik: f.jezik,
    p_kontakt_ime: f.kontakt_ime,
    p_email: f.email,
    p_telefon: f.telefon ?? '',
    p_splet: f.splet ?? '',
    p_st_tecajnikov: f.st_tecajnikov ?? '',
    p_program: f.program ?? '',
    p_sporocilo: f.sporocilo ?? '',
    p_vir: f.vir ?? '',
  });
  must(error, 'Prijave ni bilo mogoče oddati.');
  return data;
}
